import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { Route, useHistory, useRouteMatch } from "react-router-dom"

import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import ManageArtPieces from "./ManageArtPieces"
import { iExhibition } from "../../../definitions/Exhibition"
import {
  handleUpdateData,
  get,
  handleDeleteData,
  post,
} from "../../../store/data/actions"
import { iResponse, iUrlParams } from "../../../definitions/interfaces"

import s from "../Exhibitions.less"
import ExhibitionOverview from "./Overview"
import ExhibitionUploads from "./Uploads"
import { Controller, FormProvider, useForm } from "react-hook-form"
import Header from "../Header"
import { store } from "react-notifications-component"
import { exhibitionTypes, notificationOptions } from "../../../api/Definitions"
import { iAccess } from "../../../definitions/Access"
import { unstable_batchedUpdates } from "react-dom"
import { ItemWithAccess } from "../../../definitions/types"
import ManageArtifacts from "./ManageArtifacts"
import ManageAssets from "./ManageAsssets"
import ManageArTargets from "./ManageArTargets"
import Access from "../../../components/Access"
import ManageSpace from "./ManageSpace"

interface iExhibitionParams {
  url: string
}

interface iProps {
  item: iExhibition
  access: iAccess
  reloadExhibition: () => void
}

const ExhibitionInspector: FunctionComponent<iProps> = ({
  item,
  access,
  reloadExhibition,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iExhibitionParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iExhibition>>({
    defaultValues: { ...item, __access: access },
    shouldUnregister: false,
  })

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item])

  const onSubmit = (data: ItemWithAccess<iExhibition>) => {
    setLoading(true)
    handleUpdateData<ItemWithAccess<iExhibition>>({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iExhibition>>,
      link: "/v2/exhibition",
    })
      .then(() => {
        reloadExhibition()
        setLoading(false)
        store.addNotification({
          title: "Success!",
          message: "Exhibition successfully updated!",
          // message: "teodosii@react-notifications-component",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const handlePublish = () => {
    setLoading(true)
    post({
      link: item.published
        ? "/v2/unpublish-exhibition/" + item._id
        : "/v2/publish-exhibition/" + item._id,
    }).then(() => {
      setLoading(false)
      reloadExhibition()
    })
  }

  const slug = (type: string) => {
    const { slug } = exhibitionTypes.find((x) => x.value === type) || {}
    return slug
  }

  const visitLink = useMemo(() => {
    if (item.type && item.url && (item.mesh || item.meshguid)) {
      return `https://vrallart.com/${
        item.published
          ? `vr-exhibitions/${slug(item.type)}/`
          : `draft-exhibition/`
      }${item.url}/`
    }
    return undefined
  }, [item.type, item.url, item.mesh, item.meshguid])

  const editorLink = useMemo(() => {
    if (item.gallerySpaceId && item.url) {
      return `https://editor.vrallart.com?exhibition=${
        item.url
      }&token=${localStorage.getItem(
        // eslint-disable-next-line no-undef
        "loginKey",
      )}`
    }
    return undefined
  }, [item.url, item.gallerySpaceId])

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/exhibitions/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Exhibition successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const createAudio = (data: any) => {
    setLoading(true)

    post<any>({
      link: "/v2/exhibition-text-to-speech",
      data: { _id: item._id, data },
    })
      .then((s) => {
        setLoading(false)
        reloadExhibition()
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iExhibition>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title={"Exhibition Inspector"} />
      <Header
        access={access}
        title={item.title}
        handleSubmit={invokeSubmit}
        handleDelete={onDelete}
        handlePublish={handlePublish}
        visitLink={visitLink}
        editorLink={editorLink}
        cover={item.posterImageId}
        submitEditorText={"Editor"}
        submitVisitText={"Visit"}
        submitPublishText={item.published ? "Unpublish" : "Publish"}
        submitDeleteText={"Delete"}
        submitText={"Update"}
        published={!!item.published}
      />
      <div className={`inspector ${s.inspector}`}>
        {isLoading ? (
          <Loader />
        ) : (
          <form
            className={"inspector-form"}
            onSubmit={methods.handleSubmit((data) => {
              onSubmit(data as ItemWithAccess<iExhibition>)
            })}
          >
            <Route exact path={match.path + "/manage-artpieces"}>
              <Controller
                name="artPieces"
                control={methods.control}
                as={ManageArtPieces}
              />
            </Route>
            <Route exact path={match.path + "/space"}>
              <Controller
                name="gallerySpaceId"
                control={methods.control}
                as={ManageSpace}
              />
            </Route>
            <Route exact path={match.path + "/manage-artifacts"}>
              <Controller
                name="artifacts"
                control={methods.control}
                as={ManageArtifacts}
              />
            </Route>
            <Route exact path={match.path + "/manage-assets"}>
              <Controller
                name="assets"
                control={methods.control}
                as={ManageAssets}
              />
            </Route>
            <Route exact path={match.path + "/manage-ar-targets"}>
              <Controller
                name="arTargets"
                control={methods.control}
                as={ManageArTargets}
              />
            </Route>
            <Route exact path={match.path + "/manage-files"}>
              <ExhibitionUploads item={item} />
            </Route>
            <Route exact path={match.path}>
              <ExhibitionOverview item={item} onCreateAudio={createAudio} />
            </Route>
            <Route exact path={match.path + "/access"}>
              <Access access={access} />
            </Route>
            <button style={{ display: "none" }} type="submit">
              Submit
            </button>
          </form>
        )}
      </div>
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iExhibition>()
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()

  const loadExhibition = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { url: match.params.url, collection: "Exhibitions" },
      }),
      get<iResponse<iExhibition>>({
        link: "/v2/exhibitions/" + match.params.url,
      }),
    ])
      .then((result) => {
        const [accessResult, dataResult] = result
        unstable_batchedUpdates(() => {
          setItem(dataResult.data)
          setAccess(accessResult.data)
          setLoading(false)
        })
      })
      .catch((error) => {
        if (error?.status === 403) {
          history.replace("/no-permission")
        }
      })
  }

  useMemo(() => {
    loadExhibition()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <ExhibitionInspector
      item={item}
      access={access}
      reloadExhibition={loadExhibition}
    />
  ) : null
}

export default InspectorLoader
